import { styled, css, keyframes } from 'next-yak'

import * as COLORS from '../constants/colors'
import { withPrivatePublicMap } from '@owl-nest/yak/extra-runtime'

const sweepAnimation = keyframes`
    0% { background-position: -600px 0; }
    100% { background-position: 600px 0; }
`

export const skeletonAnimationRules = css`
  animation: ${sweepAnimation} 1s infinite linear forwards;
  background: ${COLORS.PRIMARY_SAND_200};
  background-image: linear-gradient(
    to right,
    ${COLORS.PRIMARY_SAND_200},
    ${COLORS.SECONDARY_GREY_400} 20%,
    ${COLORS.PRIMARY_SAND_200} 40%,
    ${COLORS.PRIMARY_SAND_200} 100%
  );
  background-repeat: no-repeat;
`

type CircleSkeletonProps = {
  radius?: number
}

export const Circle = styled.div<CircleSkeletonProps>`
  background-color: ${COLORS.PRIMARY_SAND_200};
  border-radius: 50%;

  height: ${({ radius = 32 }) => radius}px;
  width: ${({ radius = 32 }) => radius}px;

  ${skeletonAnimationRules}
`

type RectangleSkeletonProps = {
  height?: number
  width?: number
}

export const Rectangle = styled.div<RectangleSkeletonProps>`
  background-color: ${COLORS.PRIMARY_SAND_200};

  height: ${({ height = 32 }) => height}px;
  width: ${({ width = 100 }) => width}%;

  ${skeletonAnimationRules}
`

export const RoundedRectangle = styled(Rectangle)`
  border-radius: 12px;
`

export const Placeholder = styled.div<RectangleSkeletonProps>`
  border: 1px solid ${COLORS.PRIMARY_SAND_200};
  background: transparent;

  height: ${({ height = 32 }) => height}px;
  width: 100%;
`

export const SquareCard = styled.div`
  border-radius: 12px;
  background-color: ${COLORS.PRIMARY_SAND_200};
  height: 0;
  padding-bottom: 100%;
  position: relative;
  width: 100%;

  min-height: 270px;
  min-width: 270px;

  ${skeletonAnimationRules}
`

export const Tag = styled.div`
  background-color: ${COLORS.PRIMARY_SAND_200};
  border-radius: 12px;
  height: 20px;
  width: 32%;

  & + * {
    margin-top: 10px;
  }

  ${skeletonAnimationRules}
`

type TextSkeletonProps = {
  size?: 'small' | 'medium' | 'large'
  width?: number
}

type PrivateTextSkeletonProps = { [K in keyof TextSkeletonProps as `$${K}`]: TextSkeletonProps[K] }

const PrivateText = styled.div<PrivateTextSkeletonProps>`
  background-color: ${COLORS.PRIMARY_SAND_200};
  width: ${({ $width = 100 }) => $width}%;

  ${({ $size = 'medium' }) => {
    if ($size === 'large') {
      return css`
        height: 40px;
      `
    } else if ($size === 'small') {
      return css`
        height: 10px;
      `
    }

    return css`
      height: 20px;
    `
  }}
  & + & {
    margin-top: 10px;
  }

  ${skeletonAnimationRules}
`

export const Text = styled(
  withPrivatePublicMap(PrivateText, ({ size, width, ...props }: TextSkeletonProps) => ({
    ...props,
    $size: size,
    $width: width,
  })),
)``
